import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Text, Flex} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FitIcon} from '../icons'

const FIT_MESSAGES = {
    1: {id: 'product_view.actual_fit.runs_small', defaultMessage: 'Actual fit: Runs Small'},
    2: {
        id: 'product_view.actual_fit.slightly_smaller',
        defaultMessage: 'Actual fit: Slightly smaller'
    },
    3: {
        id: 'product_view.actual_fit.true_to_size',
        defaultMessage: 'Actual fit: True to size'
    },
    4: {
        id: 'product_view.actual_fit.slightly_larger',
        defaultMessage: 'Actual fit: Slightly larger'
    },
    5: {id: 'product_view.actual_fit.runs_large', defaultMessage: 'Actual fit: Runs Large'}
}

/**
 * ActualFit - Displays product fit information with optional icon
 * @param {string|number} fit - Fit value (1-5)
 * @param {boolean} showIcon - Show/hide fit icon
 * @param {Function} onClick - Click handler
 * @param {Object} containerProps - Props for Flex container
 * @param {Object} textProps - Props for Text component
 */
const ActualFitMessage = ({
    fit,
    showIcon = false,
    onClick,
    containerProps = {},
    textProps = {}
}) => {
    const intl = useIntl()

    const getFitMessage = (fitValue) => FIT_MESSAGES[Math.floor(fitValue)]

    if (!fit) return null

    const fitMessage = getFitMessage(Number(fit))
    if (!fitMessage) return null

    const fullMessage = intl.formatMessage(fitMessage)
    const [label, value] = fullMessage.split(':').map((part) => part.trim())

    return (
        <Flex
            alignItems={'center'}
            gap={'6px'}
            onClick={onClick}
            onKeyDown={(e) => e.key === 'Enter' && onClick?.(e)}
            role="button"
            tabIndex={0}
            aria-label={fullMessage}
            cursor={onClick ? 'pointer' : 'auto'}
            {...containerProps}
        >
            {showIcon && <FitIcon w={'14px'} h={'14px'} />}
            <Text
                fontSize="xs"
                fontStyle="normal"
                fontWeight={400}
                lineHeight="15px"
                {...textProps}
            >
                {label}:{' '}
                <Text as="span" textDecoration="underline">
                    {value}
                </Text>
            </Text>
        </Flex>
    )
}

ActualFitMessage.propTypes = {
    /** Fit value (1-5) corresponding to different fit types */
    fit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Show/hide fit icon - defaults to false */
    showIcon: PropTypes.bool,
    /** Click handler function */
    onClick: PropTypes.func,
    /** Additional props to pass to the Flex container */
    containerProps: PropTypes.object,
    /** Additional props to pass to the Text component */
    textProps: PropTypes.object
}

export default ActualFitMessage
